import { useState, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import type { Project, Repository } from '../lib/data';
import {
  existingProjectRepositoryRequest,
  projectRegistrationRequest,
  saveExistingProjectRepositoryOnce,
  saveProjectRegistrationOnce,
  type ExistingProjectRepositoryRequest,
  type ProjectRegistrationRequest,
  type RegisteredProjectRepository,
} from '../lib/project-registration';

export function ProjectRepositoryList({ projects, repositories, register }: {
  readonly projects: readonly Project[];
  readonly repositories: readonly Repository[];
  /** What a project with no repository offers under its name; nothing when omitted. */
  readonly register?: (project: Project) => ReactNode;
}) {
  if (projects.length === 0) return <p className="muted">No projects are registered.</p>;
  return (
    <ul className="stack" aria-label="Registered project repositories">
      {projects.map((project) => {
        const rows = repositories.filter((repository) => repository.project_id === project.id);
        return (
          <li key={project.id} className="stack">
            <strong>{project.name}</strong>
            <small className="muted">Project ID: <code>{project.id}</code></small>
            {rows.length === 0
              ? <span className="muted">No repository registered.</span>
              : rows.map((repository) => (
                <span key={repository.id} className="stack">
                  <span>{repository.name} · default {repository.default_ref ?? 'not set'}</span>
                  <small className="muted">Repository ID: <code>{repository.id}</code></small>
                </span>
              ))}
            {rows.length === 0 && register?.(project)}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Gives one existing project its first repository. The request names the project by its ID,
 * so nothing is matched by name, and it goes through the same verified owner session as the
 * registration below; the database checks both again and never replaces a repository.
 */
export function ExistingProjectRepositoryForm({ client, guard, orgId, project, onRegistered }: {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly project: Project;
  readonly onRegistered: (value: RegisteredProjectRepository) => Promise<void>;
}) {
  const [repositoryName, setRepositoryName] = useState('');
  const [defaultRef, setDefaultRef] = useState('main');
  const [pending, setPending] = useState<ExistingProjectRepositoryRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const preview = existingProjectRepositoryRequest({
    orgId, projectId: project.id, repositoryName, defaultRef,
    requestId: '00000000-0000-4000-8000-000000000000',
  });

  const change = (set: (value: string) => void, value: string) => {
    set(value);
    setPending(null);
    setMessage(null);
  };
  const write = (request: ExistingProjectRepositoryRequest) => {
    void guard('post_owner_command', async () => {
      setBusy(true);
      setMessage(null);
      try {
        const result = await saveExistingProjectRepositoryOnce(client, request);
        if (result.outcome === 'unknown') setPending(request);
        else setPending(null);
        if (result.outcome === 'saved' && result.value) await onRegistered(result.value);
        else setMessage(result.message);
      } finally {
        setBusy(false);
      }
    });
  };

  // A disclosure, not a button: closed, it adds nothing to press by accident.
  return (
    <details className="project-repository-existing">
      <summary>Register a repository for this project</summary>
      <form className="stack" onSubmit={(event) => {
        event.preventDefault();
        const request = pending ?? existingProjectRepositoryRequest({
          orgId, projectId: project.id, repositoryName, defaultRef, requestId: globalThis.crypto.randomUUID(),
        });
        if (request !== null) write(request);
      }}>
        <small className="muted">
          {project.name} keeps its name, ID, workspaces and computer folders. Only the repository is added, and a
          registered repository is never replaced.
        </small>
        <label className="stack">
          <span>Repository (owner/repository)</span>
          <input aria-label={`Repository owner/name for ${project.name}`} maxLength={201} value={repositoryName}
            autoComplete="off" spellCheck={false} placeholder="owner/repository"
            onChange={(event) => change(setRepositoryName, event.target.value)} />
        </label>
        <label className="stack">
          <span>Default branch</span>
          <input aria-label={`Default branch for ${project.name}`} maxLength={250} value={defaultRef}
            autoComplete="off" spellCheck={false} onChange={(event) => change(setDefaultRef, event.target.value)} />
        </label>
        <button type="submit" className="primary" disabled={busy || (pending === null && preview === null)}>
          {pending === null ? 'Register repository' : 'Retry same registration'}
        </button>
        {message !== null && <p role="alert">{message}</p>}
      </form>
    </details>
  );
}

export function ProjectRepositorySettings({ client, guard, orgId, projects, repositories, onChanged }: {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly projects: readonly Project[];
  readonly repositories: readonly Repository[];
  readonly onChanged: () => Promise<void>;
}) {
  const [projectName, setProjectName] = useState('');
  const [repositoryName, setRepositoryName] = useState('');
  const [defaultRef, setDefaultRef] = useState('main');
  const [pending, setPending] = useState<ProjectRegistrationRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const preview = projectRegistrationRequest({
    orgId, projectName, repositoryName, defaultRef,
    requestId: '00000000-0000-4000-8000-000000000000',
  });

  const change = (set: (value: string) => void, value: string) => {
    set(value);
    setPending(null);
    setMessage(null);
  };
  const write = (request: ProjectRegistrationRequest) => {
    void guard('post_owner_command', async () => {
      setBusy(true);
      setMessage(null);
      try {
        const result = await saveProjectRegistrationOnce(client, request);
        if (result.outcome === 'unknown') setPending(request);
        else setPending(null);
        if (result.outcome === 'saved' && result.value) {
          setMessage(`Registered ${result.value.projectName} and ${result.value.repositoryName}.`);
          await onChanged();
        } else setMessage(result.message);
      } finally {
        setBusy(false);
      }
    });
  };

  return (
    <section className="stack project-repository-settings" aria-label="Project and repository registration">
      <p className="muted">
        Register only the canonical project and repository identities. Computer folders, workspaces, budgets,
        dispatch and merge routes stay in their existing controls below.
      </p>
      <ProjectRepositoryList projects={projects} repositories={repositories} register={(project) => (
        <ExistingProjectRepositoryForm client={client} guard={guard} orgId={orgId} project={project}
          onRegistered={async (value) => {
            setAdded(`Registered ${value.repositoryName} for ${value.projectName}.`);
            await onChanged();
          }} />
      )} />
      {added !== null && <p role="status">{added}</p>}
      <form className="stack" onSubmit={(event) => {
        event.preventDefault();
        const request = pending ?? projectRegistrationRequest({
          orgId, projectName, repositoryName, defaultRef, requestId: globalThis.crypto.randomUUID(),
        });
        if (request !== null) write(request);
      }}>
        <label className="stack">
          <span>Project name</span>
          <input aria-label="Project name" maxLength={120} value={projectName} autoComplete="off"
            onChange={(event) => change(setProjectName, event.target.value)} />
        </label>
        <label className="stack">
          <span>Repository (owner/repository)</span>
          <input aria-label="Repository owner/name" maxLength={201} value={repositoryName} autoComplete="off" spellCheck={false}
            placeholder="owner/repository" onChange={(event) => change(setRepositoryName, event.target.value)} />
        </label>
        <label className="stack">
          <span>Default branch</span>
          <input aria-label="Default branch" maxLength={250} value={defaultRef} autoComplete="off" spellCheck={false}
            onChange={(event) => change(setDefaultRef, event.target.value)} />
        </label>
        <button type="submit" className="primary" disabled={busy || (pending === null && preview === null)}>
          {pending === null ? 'Register project and repository' : 'Retry same registration'}
        </button>
        {message !== null && <p role="alert">{message}</p>}
      </form>
    </section>
  );
}
